import axios from "@/plugins/axios";
import { tokenManager } from "@/app/shared/utils/tokenManager";
import EventBus from "@/app/shared/utils/eventBus";
import { WorkspaceEvent } from "@/app/workspace/domain/events";

const setGlobalDownloadState = (isDownloading) =>
  EventBus.emit(WorkspaceEvent.CHANGED_GLOBAL_DOWNLOAD_STATE, isDownloading);

export class ResponseError extends Error {
  constructor(error) {
    console.log("ResponseError", error);
    const message = error?.data?.message || error?.message || "Error desconocido";
    super(message);
    // super(error.data.message);
    this.status = error?.status || null;
    this.data = error?.data || {};
    this.message = message;
    this.errors = error?.data?.errors || [];
    this.details = error?.data?.detail || {};
    this.config = error?.config;
    this.responseUrl = error?.request?.responseURL || null;
  }

  getErrorMessage(customMessage, showKey = true) {
    const defaultMessage = customMessage || "Ha ocurrido un error";
    
    if (!this.errors?.length)
      return JSON.stringify(this.data)
        .replace(/[{}[\]"]/g, "")
        .replace(/,/g, " ");

    return showKey
      ? this.errors?.join(", ") || defaultMessage
      : Object.values(this.details).join(", ") || defaultMessage;
  }
  getCleanErrorMessage(error) {
    const defaultMessage = this.getErrorMessage(error);
    return defaultMessage?.replace(/^[^:]+:\s*/, '') || error;
  }
  getError(customMessage) {
    const defaultMessage = customMessage || "Ha ocurrido un error";
    if (!this.errors?.length) return this.data;

    return { error: this.errors?.join(", ") || defaultMessage };
  }
  getErrorDetails() {
    return {
      status: this.status,
      url: this.responseUrl,
      method: this.config?.method,
      data: JSON.parse(this.config?.data),
      response: this.data,
    }
  }
}
class HttpManager {
  init(baseURL) {
    axios.defaults.baseURL = baseURL;
    // axios.defaults.baseURL = `http://${localStorage.getItem("workspace")}.localhost:8000/`; // retirar
  }

  updateBaseURL(baseURL) {
    let finalBaseURL = baseURL;

    // ASEGURAR QUE EL BASE URL SEA VÁLIDO Y NO CONTENGA "null."
    if (!baseURL || baseURL.includes("null.")) {
      const workspaceFromStorage = localStorage.getItem("workspace");
      let workspace = workspaceFromStorage;

      // fallback: extraer del subdominio
      if (!workspace) {
        const hostname = window.location.hostname;
        // ej: codebeex.beexcontact.dev → codebeex
        const parts = hostname.split(".");
        if (parts.length > 2) {
          workspace = parts[0];
        }
      }

      if (workspace) {
        localStorage.setItem("workspace", workspace);
        finalBaseURL = `https://${workspace}.${import.meta.env.VITE_HOSTNAME}`;
      } else {
        finalBaseURL = `https://backend.${import.meta.env.VITE_HOSTNAME}`;
      }
    }

    axios.defaults.baseURL = finalBaseURL;
    // axios.defaults.baseURL = `http://${localStorage.getItem("workspace")}.localhost:8000/`; // retirar
  }

  getHTTPClient(options) {
    // const accessToken = tokenManager.getToken();

    // if (accessToken) axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

    // if (options.isPublic) {
    //   this.removeHeader();
    //   axios.defaults.headers.common["Authorization"] = `Basic ${import.meta.env.VITE_PUBLIC_TOKEN}`;
    // }
    console.log("HTTP MANAGER", options);
    axios.defaults.isPublic = options.isPublic ?? false;

    if (options.authData) {
      this.removeHeader();
      axios.defaults.headers.common["Authorization"] = `bearer ${options.authData}`;
    }

    return axios;
  }

  setHeader() {
    axios.defaults.headers.common["Authorization"] = `Bearer ${tokenManager.getToken()}`;
  }

  removeHeader() {
    axios.defaults.headers.common = {};
  }

  getErrorDefault(error) {
    // Error sin respuesta (problema de red, CORS, cancelación)
    return {
      status: 0,
      data: { message: error?.message || 'Error desconocido error default' },
      config: error?.config,
      headers: {},
    };
  }

  get(resource, options = {}) {
    // Se reenvía `params` como config de axios: hasta ahora se descartaba y los
    // llamadores que filtraban por query string (`start_date`, `period`) no filtraban
    // nada, en silencio. `options` sigue sirviendo para elegir el cliente
    // (`isPublic`), que no debe viajar como config de la petición.
    return this.getHTTPClient(options)
      .get(resource, { params: options.params })
      .catch((error) => {
        throw new ResponseError(error?.response || this.getErrorDefault(error));
      });
  }

  getBlob(resource, options = {}) {
    setGlobalDownloadState(true);

    return this.getHTTPClient(options)
      .get(resource, {
        // Igual que en `get()`: `params` debe viajar como config de axios, si no los
        // filtros por query string (p. ej. el rango de fechas) se descartan en silencio.
        params: options.params,
        responseType: "blob",
      })
      .then((response) => {
        setGlobalDownloadState(false);
        return response;
      })
      .catch((error) => {
        setGlobalDownloadState(false);
        throw new ResponseError(error?.response || this.getErrorDefault(error));
      });
  }

  upload(endpoint, data, options = {}) {
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        ...options.headers,
      },
      onUploadProgress: options.onUploadProgress,
    };

    if (options.responseType) config.responseType = options.responseType;

    return this.getHTTPClient(options).post(endpoint, data, config);
  }

  uploadPut(endpoint, data, options = {}) {
    return this.getHTTPClient(options)
      .put(endpoint, data, {
        headers: {
          ...options.headers,
          "Content-Type": "multipart/form-data",
        },
      })
      .catch((error) => {
        throw new ResponseError(error?.response || this.getErrorDefault(error));
      });
  }

  post(resource, data, options = {}) {
    if (options.blob) setGlobalDownloadState(true);

    return this.getHTTPClient(options)
      .post(resource, data, { responseType: options.blob ? "blob" : "json" })
      .then((response) => {
        if (options.blob) setGlobalDownloadState(false);
        return response;
      })
      .catch((error) => {
        if (options.blob) setGlobalDownloadState(false);
        throw new ResponseError(error?.response || this.getErrorDefault(error));
      });
  }

  put(resource, data, options = {}) {
    return this.getHTTPClient(options)
      .put(resource, data)
      .catch((error) => {
        throw new ResponseError(error?.response || this.getErrorDefault(error));
      });
  }

  patch(resource, data, options = {}) {
    return this.getHTTPClient(options)
      .patch(resource, data)
      .catch((error) => {
        throw new ResponseError(error?.response || this.getErrorDefault(error));
      });
  }

  delete(resource, data, options = {}) {
    return this.getHTTPClient(options)
      .delete(resource, {
        data,
      })
      .catch((error) => {
        throw new ResponseError(error?.response || this.getErrorDefault(error));
      });
  }
}

export default new HttpManager();
